/* =========================================================================
   Career-progression client (PLAN-progression.md §1 / §5) — the two read-only
   endpoints the home screen and the round-end XP moment need:

     GET /api/game/progress        → xp + career level + rank
     GET /api/game/session/active  → the resumable server-saved session (or null)

   EVERYTHING here fails soft. Guests never fetch at all; a 404 (endpoint not
   deployed yet), a 401, a malformed body or a dead network all resolve to
   `null`, and the callers then render nothing new — home looks exactly like
   today's home. Progress UI must never be able to break the app.

   AUTH NOTE (read api.ts, don't edit it): api.ts keeps the JWT access token in a
   module-private variable and exposes no accessor, so this module cannot attach
   a Bearer header on its own. It therefore rides api.ts itself: when api.ts
   exports a helper for one of these endpoints (it already does for the active
   session — `getActiveGameSession`), we call it and get the full Bearer +
   401-refresh-retry behaviour for free. The helper is looked up off the module
   namespace at call time, so a helper that doesn't exist yet is simply "not
   there" instead of a compile error. Only then do we fall back to a bare
   same-origin fetch, which will 401 and fail soft against a Bearer-only server —
   i.e. an endpoint with no api.ts helper renders nothing until one exists.
   Injecting `setProgressTransport()` overrides both (used by the tests).

   Pure shape-normalisation (`normalizeProgress` / `normalizeActiveSession`) is
   unit-tested; the fetch seam around it is deliberately thin.
   ========================================================================= */
import * as api from './api';
import { rankForXp } from './data/ranksData';

const API_BASE = '/api';
const REQUEST_TIMEOUT_MS = 8000;
/** Home can repaint several times per visit; don't re-hit the API each time. */
const CACHE_TTL_MS = 15000;

/** GET /api/game/progress (camel-cased server DTO). */
export interface GameProgress {
  xpTotal: number;
  /** Career level (PLAN §1) — the 30-level ladder. Always >= 1. */
  careerLevel: number;
  xpIntoLevel: number;
  /** Size of the current level in XP (0 when the server doesn't send one). */
  xpToNextLevel: number;
  /** The permanent 25-rank scholarly track (data/ranksData.ts). */
  rankLevel: number;
  rankTitle: string;
}

/** GET /api/game/session/active — null when there is nothing to resume. */
export interface ActiveSession {
  sessionId: string;
  /** 0-based index of the round to resume ON (round 3 of 5 → roundIndex 2). */
  roundIndex: number;
  bankCents: number;
  hardMode: boolean;
}

/* ------------------------------- pure logic ------------------------------- */

function obj(raw: unknown): Record<string, unknown> | null {
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
}

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** Normalise the progress DTO. Missing/garbage fields fall back (rank is derived
    from lifetime XP through the bundled ladder when the server omits it), but a
    body carrying NEITHER xpTotal nor careerLevel is not progress at all → null,
    and the caller renders nothing. PURE. */
export function normalizeProgress(raw: unknown): GameProgress | null {
  const o = obj(raw);
  if (!o) return null;
  const hasXp = typeof o.xpTotal === 'number' && Number.isFinite(o.xpTotal);
  const hasLevel = typeof o.careerLevel === 'number' && Number.isFinite(o.careerLevel);
  if (!hasXp && !hasLevel) return null;

  const xpTotal = Math.max(0, Math.round(num(o.xpTotal, 0)));
  const careerLevel = Math.max(1, Math.round(num(o.careerLevel, 1)));
  const xpToNextLevel = Math.max(0, Math.round(num(o.xpToNextLevel, 0)));
  let xpIntoLevel = Math.max(0, Math.round(num(o.xpIntoLevel, 0)));
  if (xpToNextLevel > 0 && xpIntoLevel > xpToNextLevel) xpIntoLevel = xpToNextLevel;

  const fallbackRank = rankForXp(xpTotal);
  const title = typeof o.rankTitle === 'string' && o.rankTitle.trim() ? o.rankTitle.trim() : fallbackRank.name;
  const rankLevel = Math.max(1, Math.round(num(o.rankLevel, fallbackRank.level)));

  return { xpTotal, careerLevel, xpIntoLevel, xpToNextLevel, rankLevel, rankTitle: title };
}

/** Normalise the active-session DTO. A 200-with-null body ("nothing saved") and
    a session without an id are both `null` — the Continue card only ever shows
    for a session we could actually name. PURE. */
export function normalizeActiveSession(raw: unknown): ActiveSession | null {
  const o = obj(raw);
  if (!o) return null;
  const sessionId = typeof o.sessionId === 'string' ? o.sessionId.trim() : '';
  if (!sessionId) return null;
  return {
    sessionId,
    roundIndex: Math.max(0, Math.round(num(o.roundIndex, 0))),
    bankCents: Math.round(num(o.bankCents, 0)),
    hardMode: o.hardMode === true,
  };
}

/* ------------------------------- fetch seam ------------------------------- */

/** Injectable transport (tests / a future host). Resolves the parsed JSON body,
    or `undefined` for "no answer" (offline, 4xx, 5xx). */
export type ProgressTransport = (path: string) => Promise<unknown>;

let transport: ProgressTransport | null = null;

/** Override how progress requests are made (tests inject a stub). */
export function setProgressTransport(t: ProgressTransport | null): void {
  transport = t;
}

/** Unwrap api.ts's ApiResult<T> discriminated union (or a bare body). */
function unwrapApiResult(r: unknown): unknown {
  const o = obj(r);
  if (o && typeof o.ok === 'boolean') return o.ok ? o.data : undefined;
  return r;
}

/** Call an api.ts helper for this endpoint if one exists under any of `names`
    (looked up at call time — see the AUTH NOTE). `{ hit:false }` = none, so the
    caller falls back to a bare fetch. */
async function viaApiModule(names: string[]): Promise<{ hit: boolean; body?: unknown }> {
  const mod = api as unknown as Record<string, unknown>;
  for (const n of names) {
    const fn = mod[n];
    if (typeof fn !== 'function') continue;
    try {
      const r = await (fn as () => Promise<unknown>)();
      return { hit: true, body: unwrapApiResult(r) };
    } catch {
      return { hit: true, body: undefined };
    }
  }
  return { hit: false };
}

/** Bare same-origin GET. No Bearer available here (see the header note) — this
    is the last-resort path and is expected to fail soft on a Bearer-only API. */
async function bareGet(path: string): Promise<unknown> {
  const ctrl = typeof AbortController === 'undefined' ? null : new AbortController();
  const timer = ctrl ? setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS) : null;
  try {
    const res = await fetch(API_BASE + path, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctrl ? { signal: ctrl.signal } : {}),
    });
    if (!res.ok) return undefined; // 401 / 404 (not deployed yet) / 5xx
    const text = await res.text();
    if (!text) return null; // 200 with an empty body = "nothing here"
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return undefined;
    }
  } catch {
    return undefined; // offline / timeout / CORS
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function get(path: string, apiNames: string[]): Promise<unknown> {
  if (transport) {
    try {
      return await transport(path);
    } catch {
      return undefined;
    }
  }
  const viaApi = await viaApiModule(apiNames);
  if (viaApi.hit) return viaApi.body;
  return bareGet(path);
}

/* ------------------------------ public calls ------------------------------ */

let cache: { progress: GameProgress | null; at: number } | null = null;

/** Drop the memoised progress (sign-in / sign-out / after an XP award). */
export function forgetProgress(): void {
  cache = null;
}

/** Last known progress WITHOUT a request — lets home paint instantly on a
    repeat visit and refresh underneath (the renderStreakChip pattern). */
export function cachedProgress(): GameProgress | null {
  return cache ? cache.progress : null;
}

/** GET /api/game/progress. Guests and every failure mode resolve to null. */
export async function fetchProgress(force = false): Promise<GameProgress | null> {
  if (!api.isAuthenticated()) {
    cache = null;
    return null;
  }
  if (!force && cache && Date.now() - cache.at < CACHE_TTL_MS) return cache.progress;
  const body = await get('/game/progress', ['getGameProgress', 'getProgress', 'getCareerProgress']);
  const progress = normalizeProgress(body);
  cache = { progress, at: Date.now() };
  return progress;
}

/** GET /api/game/session/active. Null = nothing to continue (or unavailable). */
export async function fetchActiveSession(): Promise<ActiveSession | null> {
  if (!api.isAuthenticated()) return null;
  const body = await get('/game/session/active', [
    'getActiveGameSession',
    'getActiveSession',
    'getGameSessionActive',
  ]);
  return normalizeActiveSession(body);
}
